import React, { useState } from "react";
import { useTheme as useAppTheme } from "../contexts/ThemeContext";
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { createCommonStyles } from "../theme/styles";
import Logo from "../assets/ZakazujMeLogo4.png";
import { useLocation } from "react-router-dom";
import authService from "../services/authService";
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {
  const { isDarkMode } = useAppTheme();
  const commonStyles = createCommonStyles(isDarkMode);
  const [form, setForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState({});

  const location = useLocation();
  const navigate = useNavigate();

  const userId = location.state?.userId;

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.password || !form.confirmPassword)
      return setError("Unesite i potvrdite novu lozinku.");
    if (form.password !== form.confirmPassword)
      return setError("Lozinke se ne poklapaju.");

    try {
      setIsLoading(true);
      const data = {
        newPassword: form.password,
        userId: userId,
      };
      const result = await authService.resetPassword(data);
      setResult(result);
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      setError(err?.message || "Došlo je do greške. Pokušajte ponovo.");
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Grid container sx={{ minHeight: "100vh", overflow: "hidden" }}>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 3, sm: 5 },
          py: { xs: 4, sm: 6 },
          background:
            "radial-gradient(1200px 800px at 20% 30%, #1e2a78 0%, #0b0f2b 50%, #0a0d20 100%)",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(600px 380px at 45% 40%, rgba(26,86,219,.55), transparent 60%), radial-gradient(620px 420px at 65% 60%, rgba(168,85,247,.45), transparent 62%), radial-gradient(580px 360px at 75% 30%, rgba(6,182,212,.4), transparent 62%)",
            filter: "blur(18px)",
            opacity: 0.9,
          }}
        />
        <Box
          component="img"
          src={Logo}
          alt="Zakazuj"
          sx={{
            position: "relative",
            height: { xs: 140, sm: 200, md: 320, lg: 380 },
            objectFit: "contain",
            filter: "drop-shadow(0 0 22px rgba(124,58,237,0.6))",
          }}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "center", md: "flex-start" },
          px: { xs: 3, sm: 6, md: 6, lg: 8 },
          py: { xs: 6, md: 0 },
          background: isDarkMode 
            ? "linear-gradient(180deg, #0F1431 0%, #171B46 100%)"
            : "#ffffff",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            ...commonStyles.glassContainerMedium,
            p: { xs: 3, sm: 4 },
            maxWidth: 520,
            width: "100%",
            borderRadius: 3,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "rgba(255,255,255,0.95)",
              fontWeight: 900,
              letterSpacing: "-0.02em",
              mb: 1,
            }}
          >
            Zaboravljena lozinka
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.75)", mb: 2 }}>
            Unesite novu lozinku.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {result.success && (
            <Alert
              severity="success"
              sx={{ mb: 2, ...commonStyles.alert("success") }}
            >
              {result.message}
            </Alert>
          )}

          <Box component="form" noValidate onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="password"
              name="password"
              label="Nova lozinka"
              type={showPwd ? "text" : "password"}
              autoComplete="new-password"
              value={form.password}
              onChange={handleChange}
              disabled={isLoading}
              variant="outlined"
              sx={commonStyles.formField}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPwd((s) => !s)}
                      edge="end"
                      sx={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      {showPwd ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="confirmPassword"
              name="confirmPassword"
              label="Potvrdi lozinku"
              type={showConfirm ? "text" : "password"}
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={handleChange}
              disabled={isLoading}
              variant="outlined"
              sx={commonStyles.formField}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirm((s) => !s)}
                      edge="end"
                      sx={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                ...commonStyles.gradientButton,
                mt: 2,
                py: 1.25,
                borderRadius: 999,
                fontWeight: 800,
                textTransform: "none",
              }}
            >
              {isLoading ? (
                <CircularProgress size={22} sx={{ color: "white" }} />
              ) : (
                "Resetuj lozinku"
              )}
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;
